import React, { forwardRef, useImperativeHandle, useEffect, useState } from 'react';
import { Formik, Form, Field } from 'formik';
import { OtherInfo, getProjectFiltersConfig } from '../project.type'; // Import your type
import { Autocomplete, Grid, TextField } from '@mui/material';
import DynamicDatePicker from '../../common/DynamicDatePicker';
import { toUpperCaseFirstChar } from '../project.util';
import { FieldProps } from 'formik';
import { getAllObservers } from '../project.api';
import { UserDTO } from '../../user-management/auth.type';

interface Props {
  otherInfo: OtherInfo;
  onUpdate: (data: OtherInfo) => void;
}

export interface OtherProjectInfoRef {
  submitForm: () => void;
}

export const OtherProjectInfo = forwardRef<OtherProjectInfoRef, Props>(({ otherInfo, onUpdate }, ref) => {
  const formikRef = React.useRef<any>();
  const projectFiltersConfig = getProjectFiltersConfig();
  const [availableObservers, setAvailableObservers] = useState<UserDTO[]>([]);

  // Fetch all available observers when component loads
  useEffect(() => {
    const fetchObservers = async () => {
      try {
        const observers = await getAllObservers();
        setAvailableObservers(observers);
      } catch (error) {
        console.error('Failed to fetch observers:', error);
      }
    };

    fetchObservers();
  }, []);

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      if (formikRef.current) {
        formikRef.current.submitForm();
      }
    },
  }));

  const renderField = (key: React.Key | null | undefined) => {
    if (key === 'loanOfficerName') {
      return null;
    }

    // Special handling for networkerName field
    if (key === 'networkerName') {
      return (
        <Grid size={{ xs: 12 }} key={key}>
          <Field name={key}>
            {({ field, form }: FieldProps) => (
              <TextField
                {...field}
                value={field.value || ''}
                fullWidth
                label='Networker Name'
                variant='outlined'
                margin='normal'
                onChange={event => {
                  const name = event.target.value;
                  const wasEmpty = !field.value;
                  form.setFieldValue(field.name, name);
                  if (wasEmpty && name) {
                    form.setFieldValue('networkerStatus', 'TRUE');
                  }
                }}
              />
            )}
          </Field>
        </Grid>
      );
    }

    // Special handling for networkerStatus field
    if (key === 'networkerStatus') {
      return (
        <Grid size={{ xs: 12 }} key={key}>
          <Field name={key}>
            {({ field, form }: FieldProps) => (
              <Autocomplete
                disablePortal
                options={['TRUE', 'FALSE', 'PARTIAL']}
                value={field.value || 'FALSE'}
                onChange={(event, newValue) => {
                  form.setFieldValue(field.name, newValue ?? 'FALSE');
                }}
                renderInput={params => <TextField {...params} label='Networker Status' variant='outlined' fullWidth margin='normal' />}
              />
            )}
          </Field>
        </Grid>
      );
    }

    // Special handling for observer field
    if (key === 'observer') {
      return (
        <Grid size={{ xs: 12 }} key={key}>
          <Field name={key}>
            {({ field, form }: FieldProps) => (
              <Autocomplete
                disablePortal
                options={availableObservers}
                getOptionLabel={option => `${option.firstName} ${option.lastName}`}
                value={availableObservers.find(observer => observer.id === field.value?.id) || null}
                onChange={(event, newValue) => {
                  form.setFieldValue(field.name, newValue);
                }}
                renderInput={params => <TextField {...params} label='Observer' variant='outlined' fullWidth margin='normal' />}
              />
            )}
          </Field>
        </Grid>
      );
    }

    // Special handling for reviewingSubcontractorLienWaivers field (boolean Yes/No)
    if (key === 'reviewingSubcontractorLienWaivers') {
      return (
        <Grid size={{ xs: 12 }} key={key}>
          <Field name={key}>
            {({ field, form }: FieldProps) => (
              <Autocomplete
                disablePortal
                options={[
                  { label: 'Yes', value: true },
                  { label: 'No', value: false },
                ]}
                getOptionLabel={option => option.label}
                value={field.value === true ? { label: 'Yes', value: true } : field.value === false ? { label: 'No', value: false } : null}
                onChange={(event, newValue) => {
                  form.setFieldValue(field.name, newValue ? newValue.value : null);
                }}
                renderInput={params => (
                  <TextField {...params} label='Reviewing Subcontractor Lien Waivers' variant='outlined' fullWidth margin='normal' />
                )}
              />
            )}
          </Field>
        </Grid>
      );
    }

    if (key === 'reviewingConstructionMeetingMinutes') {
      return (
        <Grid size={{ xs: 12 }} key={key}>
          <Field name={key}>
            {({ field, form }: FieldProps) => (
              <Autocomplete
                disablePortal
                options={[
                  { label: 'Yes', value: true },
                  { label: 'No', value: false },
                ]}
                getOptionLabel={option => option.label}
                value={field.value === true ? { label: 'Yes', value: true } : field.value === false ? { label: 'No', value: false } : null}
                onChange={(event, newValue) => {
                  form.setFieldValue(field.name, newValue ? newValue.value : null);
                }}
                renderInput={params => (
                  <TextField {...params} label='Reviewing Construction Meeting Minutes' variant='outlined' fullWidth margin='normal' />
                )}
              />
            )}
          </Field>
        </Grid>
      );
    }

    if (key === 'reviewingPotentialChangeOrders') {
      return (
        <Grid size={{ xs: 12 }} key={key}>
          <Field name={key}>
            {({ field, form }: FieldProps) => (
              <Autocomplete
                disablePortal
                options={[
                  { label: 'Yes', value: true },
                  { label: 'No', value: false },
                ]}
                getOptionLabel={option => option.label}
                value={field.value === true ? { label: 'Yes', value: true } : field.value === false ? { label: 'No', value: false } : null}
                onChange={(event, newValue) => {
                  form.setFieldValue(field.name, newValue ? newValue.value : null);
                }}
                renderInput={params => (
                  <TextField {...params} label='Reviewing Potential Change Orders' variant='outlined' fullWidth margin='normal' />
                )}
              />
            )}
          </Field>
        </Grid>
      );
    }

    if (key === 'reviewingPlanOrCost') {
      return (
        <Grid size={{ xs: 12 }} key={key}>
          <Field name={key}>
            {({ field, form }: FieldProps) => (
              <Autocomplete
                disablePortal
                options={[
                  { label: 'Yes', value: true },
                  { label: 'No', value: false },
                ]}
                getOptionLabel={option => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                value={field.value === true ? { label: 'Yes', value: true } : field.value === false ? { label: 'No', value: false } : null}
                onChange={(event, newValue) => {
                  form.setFieldValue(field.name, newValue ? newValue.value : null);
                }}
                renderInput={params => (
                  <TextField {...params} label='Reviewing Plan Or Cost' variant='outlined' fullWidth margin='normal' />
                )}
              />
            )}
          </Field>
        </Grid>
      );
    }

    let keyUppercase = '';
    if (typeof key === 'string') {
      keyUppercase = toUpperCaseFirstChar(key);
    }

    const filterConfig = projectFiltersConfig.find(config => config.propertyName === toUpperCaseFirstChar(keyUppercase));
    const isLookup = filterConfig?.type === 'lookup';

    if (isLookup && filterConfig?.lookupOptions) {
      return (
        <Grid size={{ xs: 12 }} key={key}>
          <Field name={key}>
            {({ field, form }: FieldProps) => (
              <Autocomplete
                disablePortal
                options={filterConfig.lookupOptions?.map(option => option.label) || []}
                value={field.value || ''}
                onChange={(event, newValue) => {
                  form.setFieldValue(field.name, newValue);
                }}
                renderInput={params => (
                  <TextField {...params} label={filterConfig.propertyDisplayName} variant='outlined' fullWidth margin='normal' />
                )}
              />
            )}
          </Field>
        </Grid>
      );
    } else if (filterConfig?.type === 'date') {
      // Rendering date type fields
      return (
        <Grid size={{ xs: 12 }} key={key} sx={{ mt: 1 }}>
          <Field name={key}>
            {({ field, form }: FieldProps) => (
              <DynamicDatePicker
                fieldName={field.name}
                label={filterConfig?.propertyDisplayName || String(key)}
                value={field.value || null}
                onChange={(fieldName, value) => form.setFieldValue(fieldName, value)}
              />
            )}
          </Field>
        </Grid>
      );
    } else if (filterConfig?.type === 'number') {
      // Rendering number type fields
      return (
        <Grid size={{ xs: 12 }} key={key}>
          <Field
            as={TextField}
            type='number'
            fullWidth
            label={filterConfig?.propertyDisplayName || key}
            name={key}
            variant='outlined'
            margin='normal'
          />
        </Grid>
      );
    } else {
      // For non-lookup fields, use TextField
      return (
        <Grid size={{ xs: 12 }} key={key}>
          <Field as={TextField} fullWidth label={filterConfig?.propertyDisplayName || key} name={key} variant='outlined' margin='normal' />
        </Grid>
      );
    }
  };
  if (!otherInfo) {
    // Handle the case where otherInfo is null or undefined
    return <div>N/A</div>; // or any other fallback UI
  }
  const initialValues = {
    ...otherInfo,
    networkerStatus: otherInfo.networkerStatus || (otherInfo.networkerName ? 'TRUE' : 'FALSE'),
  };
  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      onSubmit={(values, actions) => {
        onUpdate(values);
        actions.setSubmitting(false);
      }}
    >
      <Form noValidate autoComplete='off'>
        <Grid container spacing={2} style={{ maxWidth: '500px', margin: 'auto' }}>
          {Object.keys(otherInfo).map(key => renderField(key))}
        </Grid>
      </Form>
    </Formik>
  );
});

OtherProjectInfo.displayName = 'OtherProjectInfo';
